import React, {useCallback, useEffect, useState} from 'react';
import {Platform, View} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import axios, {CancelTokenSource} from 'axios';
import {useDebounce} from 'use-debounce';

import {Address as MflAddress} from '../../../models/mfl';
import {Address as MiddlewareAddress} from '../../../models/middleware';
import apiService from '../../../transport/apiService';
import {logger, serializeError} from '../../../utils/logger';

import {
  PICKER_Z_INDEX,
  PickerSearchGlyph,
  pickerWrapperStyles,
  TEXT_INSET_STYLE,
} from './PickerSearchGlyph';
import styles from './styles';

const MANUAL = 'MANUAL';
const MANUAL_ADDRESS_DROPDOWN_ITEM: MiddlewareAddress = {
  full_address: 'Enter address manually',
  lines: [MANUAL, MANUAL],
  locality: MANUAL,
  administrative_area: MANUAL,
  postal_code: MANUAL,
  id: MANUAL,
};

let cancelToken: CancelTokenSource | undefined;
const doSearchAddress = async (text = '') => {
  cancelToken?.cancel('Address search request canceled due to new request.');
  cancelToken = axios.CancelToken.source();

  const addresses = await apiService.addressSearch(text, cancelToken);
  return addresses || [];
};

const makeFullAddress = (address: MflAddress) => {
  const addressLines = address.lines || [];
  // Strip trailing punctuation and filter empty, then join with comma
  const cleanedLines = addressLines
    .filter(line => line?.trim())
    .map(line => line.trim().replace(/[.,]+$/, ''));
  const addressLinesPart = cleanedLines.length ? `${cleanedLines.join(', ')},` : '';
  const localityPart = address.locality || '';
  const administrativeAreaPart = address.administrative_area || '';
  const postalCodePart = address.postal_code || '';

  return `${addressLinesPart} ${localityPart} ${administrativeAreaPart} ${postalCodePart}`.trim();
};

// DIS-1870: forms seed an empty address ({lines: [], ...}) when there is none.
// Treat it as no selection: shown as an item, it rendered as a blank row with a
// check mark, which read as an address already chosen.
const isEmptyAddress = (address: MflAddress) =>
  (address.lines?.length ?? 0) === 0 && !(address as MiddlewareAddress).full_address;

const mflAddressToMwAddress = (address: MflAddress): MiddlewareAddress => ({
  ...address,
  full_address: (address as MiddlewareAddress).full_address || makeFullAddress(address),
  id: (address as MiddlewareAddress).id || 'FAKE_ID',
});

type Props = {
  onSelect: (address: MflAddress) => void;
  address?: MflAddress;
  placeholder?: string;
  onManualAddressEntry: () => void;
  disabled?: boolean;
  testID?: string;
};
const AddressPicker = ({
  onSelect,
  address,
  placeholder,
  onManualAddressEntry,
  disabled,
  testID,
}: Props) => {
  const initialAddress =
    address && !isEmptyAddress(address) ? mflAddressToMwAddress(address) : null;
  const initialItems = initialAddress ? [initialAddress, MANUAL_ADDRESS_DROPDOWN_ITEM] : [];
  const initialValue = initialAddress?.id || null;

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState<string | null>(initialValue);
  const [items, setItems] = useState<MiddlewareAddress[]>(initialItems);

  // Text input
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 500);

  // Sync internal state when address prop changes (e.g., modal opens with updated address)
  useEffect(() => {
    const newAddress = address && !isEmptyAddress(address) ? mflAddressToMwAddress(address) : null;
    const newValue = newAddress?.id || null;

    // Only update if the address actually changed
    if (newValue !== value) {
      const newItems = newAddress ? [newAddress, MANUAL_ADDRESS_DROPDOWN_ITEM] : [];
      setValue(newValue);
      setItems(newItems);
      setSearchText(''); // Clear any previous search
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const searchAddress = useCallback(async (text = '') => {
    if (text.length <= 3) return;

    setItems([]);
    setIsLoading(true);

    try {
      const apiAddresses = await doSearchAddress(text);
      const dropdownItems = apiAddresses.concat(MANUAL_ADDRESS_DROPDOWN_ITEM);
      setItems(dropdownItems);
    } catch (err) {
      logger.error('AddressPicker', 'Address search failed', serializeError(err));
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (debouncedSearchText) {
      searchAddress(debouncedSearchText);
    }
  }, [debouncedSearchText, searchAddress]);

  const handleSearchTextChange = (text = '') => {
    // force loading indicator to be shown even if the debouncing is not yet due
    if (text.length > 3) {
      setItems([]); // https://github.com/hossein-zare/react-native-dropdown-picker/issues/473
      setIsLoading(true);
      setSearchText(text);
    }
  };

  const handleSelectItem = (item: MiddlewareAddress) => {
    if (item.id === MANUAL) {
      onManualAddressEntry();
    } else {
      onSelect(item as MflAddress);
    }
  };

  let dropDownPickerSearchTextInputProps = {
    autoComplete: 'off',
    autoCorrect: false,
  };
  if (Platform.OS !== 'web') {
    dropDownPickerSearchTextInputProps = {
      ...dropDownPickerSearchTextInputProps,
      // On Web: autoFocus: true makes it not possible to select an option from the dropdown
      // When you select an option "nothing happens" (the dropdown disappears and the input)
      // text remains empty.
      // @ts-expect-error - Type mismatch needs investigation
      autoFocus: true,
      // On Web: onBlur makes it not possible to select an option from the dropdown
      // only "the first time". When you select an option for the first time, "nothing happens"
      // (the dropdown disappears and the input) text remains empty.
      onBlur: () => setIsOpen(false),
    };
  }

  return (
    <View style={pickerWrapperStyles.wrapper}>
      {/* DropDownPicker says the value has to be `ValueType`
      (string | number | boolean) but not really */}
      {/* @ts-expect-error - Type mismatch needs investigation */}
      <DropDownPicker<MiddlewareAddress>
        schema={{
          label: 'full_address',
          value: 'id',
        }}
        itemKey="id"
        listMode="SCROLLVIEW"
        open={isOpen}
        value={value}
        items={items}
        setOpen={setIsOpen}
        onPress={setIsOpen}
        setValue={setValue}
        setItems={setItems}
        onSelectItem={handleSelectItem}
        disableLocalSearch={true}
        searchable={true}
        placeholder={placeholder}
        searchPlaceholder={placeholder}
        searchTextInputProps={dropDownPickerSearchTextInputProps}
        loading={isLoading}
        onChangeSearchText={handleSearchTextChange}
        itemSeparator={true}
        showArrowIcon={false}
        disableBorderRadius={true}
        placeholderStyle={styles.placeholderStyle}
        style={[styles.pickerStyle, TEXT_INSET_STYLE, {display: isOpen ? 'none' : 'flex'}]}
        containerStyle={styles.containerStyle}
        labelStyle={styles.labelStyle}
        searchContainerStyle={styles.searchContainerStyle}
        searchTextInputStyle={[
          styles.searchTextInputStyle,
          TEXT_INSET_STYLE,
          isOpen ? styles.searchTextInputStyleOpen : null,
        ]}
        // SHORTCUT: colour literal — no colour token layer exists yet. Ceiling: it
        // must match placeholderStyle.color in ./styles by hand. Upgrade path: a
        // design-system colour module under app/design-system/tokens/ (DIS-1737
        // blocks it, DIS-1735 is the epic).
        searchPlaceholderTextColor="#BBB"
        dropDownContainerStyle={styles.dropDownContainerStyle}
        listItemContainerStyle={styles.listItemContainerStyle}
        listItemLabelStyle={styles.listItemLabelStyle}
        itemSeparatorStyle={styles.itemSeparatorStyle}
        zIndex={PICKER_Z_INDEX}
        // The library applies zIndexInverse (default 6000) when the list opens
        // upward, which covers the search glyph; keep both at PICKER_Z_INDEX.
        zIndexInverse={PICKER_Z_INDEX}
        scrollViewProps={{keyboardShouldPersistTaps: 'always'}}
        disabled={disabled}
        disabledStyle={styles.disabledStyle}
        textStyle={disabled ? styles.disabledStyle : undefined}
        testID={testID}
      />
      <PickerSearchGlyph
        isOpen={isOpen}
        disabled={disabled}
        testID={`${testID ?? 'address-picker'}-search-icon`}
      />
    </View>
  );
};

export default AddressPicker;
